import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  DeleteCustomTablePageDocument,
  DeleteCustomTablePageSchemaFieldFragment,
  DeleteCustomTablePageSchemaFragment,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import i18n from 'modules/core/lib/i18n'

import values from 'modules/custom_resources/store/collections/customResources/values'
import type Value from 'modules/custom_resources/store/models/customResources/Value'
import { getValue } from 'modules/employees/components/CustomResources/SchemaComponent/utils/getValue'
import { RouteContext } from 'modules/employees/pages/CustomTablesListPage/context'

interface CustomFieldValueProps {
  schema: DeleteCustomTablePageSchemaFragment | undefined
  field: DeleteCustomTablePageSchemaFieldFragment
  value?: Value
  companyLocale: string
  currency: string
}

function CustomTableValue({
  schema,
  field,
  value,
  companyLocale,
  currency,
}: CustomFieldValueProps) {
  return (
    <Box flexDirection="row" gap="s8">
      <Text.Normal weight="medium">{`${field.labelText}: `}</Text.Normal>
      <Text.Normal>
        {getValue({
          schemaEffectiveAtId: schema?.effectiveAtId,
          field,
          currentValue: value,
          notAvailable: '',
          companyLocale,
          currency,
        })}
      </Text.Normal>
    </Box>
  )
}

export default function DeleteCustomTablePage() {
  const {
    id: employeeIdparam,
    schemaId,
    valueId,
  } = useStrictParams<'id' | 'schemaId' | 'valueId'>()
  const {
    employee: sessionEmployee,
    company: { locale: companyLocale },
  } = useCurrentSession()

  const employeeId = employeeIdparam ? Number(employeeIdparam) : sessionEmployee.id

  const { data } = useQuery(DeleteCustomTablePageDocument, {
    variables: {
      schemaId: Number(schemaId),
      employeeId: employeeId,
    },
    skip: !schemaId,
  })

  const rootRoute = React.useContext(RouteContext)
  const schema = data?.customResources.schema
  const currency = data?.employees.employee?.legalEntity?.currency ?? ''

  const value = values.get(Number(valueId))
  const navigate = useNavigate()

  const onClose = () => {
    !!rootRoute && navigate(rootRoute.customTablesIndex())
  }

  const { performAction } = useAsyncAction(async (closePortal: ClosePortalFn) => {
    await value?.destroy()

    closePortal()
  })

  return (
    <Modal
      illustration="delete"
      title={i18n.t('custom_tables.delete_modal.title')}
      description={i18n.t('custom_tables.delete_modal.description')}
      onSubmit={performAction}
      onClose={onClose}
      footer={
        <Button type="negative" submit>
          {i18n.t('custom_tables.delete_modal.submit')}
        </Button>
      }
    >
      {schema?.fields?.map((resource, key) => {
        return (
          resource.field && (
            <CustomTableValue
              key={key}
              schema={schema}
              field={resource.field}
              value={value}
              companyLocale={companyLocale || ''}
              currency={currency}
            />
          )
        )
      })}
    </Modal>
  )
}
